import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaFolderOpen, FaLayerGroup, FaKey, FaLink, FaInfoCircle, FaPen, FaLock, FaBookOpen, FaListOl, FaColumns, FaHeartbeat, FaTasks, FaDownload, FaLink as FaRelation, FaTags, FaHistory } from "react-icons/fa";
import { PROJECTS_CONSTANTS } from "../_MODULES/_API";
import { PROJECT_TABS } from "../_MODULES/_Navigation";
import { STATUS_MAP_PROJECT } from "../../../_Catalogs.Views";
import { PanelSubProjects } from "../_UI/PanelSubProjects";
import { PanelCredentials } from "../_UI/PanelCredentials";
import { PanelLinks } from "../_UI/PanelLinks";
import { PanelProjectInfo } from "../_UI/PanelProjectInfo";
import { PanelGuides } from "../_UI/PanelGuides";
import { PanelSteps } from "../_UI/PanelSteps";
import { PanelTrello } from "../_UI/PanelTrello";
import { PanelRunbooks } from "../_UI/PanelRunbooks";
import { PanelProjectHealth } from "../_UI/PanelProjectHealth";
import { ProjectExportModal, ProjectRelationsModal, ProjectTagsModal, ProjectHistoryModal } from "../_UI/ProjectKnowledgeTools";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { ProjectFormModal } from "../_UI/ProjectFormModal";
import { ProjectLockScreen } from "../_UI/ProjectLockScreen";
import { useProjectAccess } from "../_Hooks/useProjectAccess";
import { COMPANIES_CONSTANTS } from "../../_Companies/_MODULES/_API";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { StatusBadge } from "../../../../../_CORE/APP/Templates/_UI/Status/_index";
import { EmptyState, Spinner, Chip } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import "../_UI/_Styles/Projects.css";

const TAB_ICONS = {
    subprojects: <FaLayerGroup />,
    steps: <FaListOl />,
    trello: <FaColumns />,
    runbooks: <FaTasks />,
    health: <FaHeartbeat />,
    credentials: <FaKey />,
    links: <FaLink />,
    info: <FaInfoCircle />,
    guides: <FaBookOpen />,
};
const TABS = PROJECT_TABS.map((item) => ({ ...item, icon: TAB_ICONS[item.value] }));

const TAB_DEFAULT = TABS[0].value;

const onResolveTab = (tab) => (TABS.some((item) => item.value === tab) ? tab : TAB_DEFAULT);

export const ProjectDetailViewMain = () => {

    const { id, tab } = useParams();

    const navigate = useNavigate();

    const ACTIVE_TAB = onResolveTab(tab);

    const TABS_LINKED = TABS.map((item) => ({ ...item, to: `/projects/${id}/${item.value}` }));

    const [useIsEditOpen, setIsEditOpen] = useState(false);
    const [useToolModal, setToolModal] = useState(null);
    const [useDescriptionExpanded, setDescriptionExpanded] = useState(false);

    const { useAccessPin, onUnlock, onLock } = useProjectAccess(id);

    const { apiGet: onGetProject, useApiData: PROJECT, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 300, enablePagination: false, startLoading: true,
    });

    const { apiGet: onGetCompanies, useApiData: COMPANIES } = useApiQuery({
        minTimeQuery: 200, enablePagination: false,
    });

    const onLoadProject = () => onGetProject({
        endpoint: PROJECTS_CONSTANTS.DETAIL, isDetail: true, config: { pathParams: { id } },
    });

    useEffect(() => {
        onLoadProject();
        onGetCompanies({ endpoint: COMPANIES_CONSTANTS.LIST });
        setDescriptionExpanded(false);
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (IS_LOADING) return <Spinner label="Cargando proyecto…" />;

    if (!PROJECT) return <EmptyState icon={<FaFolderOpen />} title="No encontramos ese proyecto" />;

    if (PROJECT.hasAccessPin && !useAccessPin) {
        return <ProjectLockScreen projectId={id} projectName={PROJECT.name} onUnlocked={onUnlock} />;
    }

    const PROJECT_DESCRIPTION = String(PROJECT.description ?? "").trim();
    const IS_LONG_DESCRIPTION = PROJECT_DESCRIPTION.length > 260;

    return (
        <ViewLayout
            header={
                <div className="LStudio__app_project_detail_header">
                    <PageHeader
                        title={PROJECT.name}
                        description={PROJECT_DESCRIPTION ? (
                            <span className="LStudio__app_project_description"
                                data-long={IS_LONG_DESCRIPTION}
                                data-expanded={useDescriptionExpanded}>
                                <span className="LStudio__app_project_description_text">{PROJECT_DESCRIPTION}</span>
                                {IS_LONG_DESCRIPTION && (
                                    <button type="button" className="LStudio__app_project_description_toggle"
                                        aria-expanded={useDescriptionExpanded}
                                        onClick={() => setDescriptionExpanded((current) => !current)}>
                                        {useDescriptionExpanded ? "Ver menos" : "Ver más"}
                                    </button>
                                )}
                            </span>
                        ) : null}
                        icon={<span className="LStudio__app_view_color_icon"
                            style={{ "--LS__item_color": PROJECT.color || "var(--LS__app_priv_accent)" }}><FaFolderOpen /></span>}
                        backTo="/projects"
                        isSticky
                        status={<StatusBadge status={PROJECT.status} map={STATUS_MAP_PROJECT} />}
                        breadcrumbs={[
                            { label: "Proyectos", href: "/projects", onClick: (event) => { event.preventDefault(); navigate("/projects"); } },
                            {
                                label: PROJECT.company?.name ?? "Compañía", href: `/companies/${PROJECT.companyId}`,
                                onClick: (event) => { event.preventDefault(); navigate(`/companies/${PROJECT.companyId}`); },
                            },
                            { label: PROJECT.name },
                        ]}
                        actions={
                            <>
                                {
                                    PROJECT.hasAccessPin && (
                                        <AppButton variant="ghost" tone="neutral" icon={<FaLock />} onClick={onLock}>
                                            Bloquear
                                        </AppButton>
                                    )
                                }

                                <CardActionMenu title="Opciones del proyecto" ariaLabel="Opciones del proyecto" items={[
                                    { key: "export", label: "Exportar proyecto completo", icon: <FaDownload />, onClick: () => setToolModal("export") },
                                    { key: "relations", label: "Relaciones y backlinks", icon: <FaRelation />, onClick: () => setToolModal("relations") },
                                    { key: "tags", label: "Etiquetas globales", icon: <FaTags />, onClick: () => setToolModal("tags") },
                                    { key: "history", label: "Historial de versiones", icon: <FaHistory />, onClick: () => setToolModal("history") },
                                ]} />
                                <AppButton variant="outline" tone="neutral" icon={<FaPen />} onClick={() => setIsEditOpen(true)}>
                                    Editar proyecto
                                </AppButton>
                            </>
                        }
                        tabs={TABS_LINKED}
                        activeTab={ACTIVE_TAB}
                        onChangeTab={(value) => navigate(`/projects/${id}/${value}`)} />
                </div>
            } >

            {
                PROJECT.stack?.length > 0 && (
                    <div className="LStudio__app_project_stack">
                        {PROJECT.stack.map((tech) => <Chip key={tech} tone="neutral">{tech}</Chip>)}
                    </div>
                )
            }

            <div className="LStudio__app_project_panel">

                {ACTIVE_TAB === "subprojects" && <PanelSubProjects project={PROJECT} />}

                {ACTIVE_TAB === "steps" && <PanelSteps projectId={id} scopeRecord={PROJECT} projectRecord={PROJECT} onScopePatched={(patch) => mutateLocal({ id: PROJECT.id, patch })} />}

                {ACTIVE_TAB === "trello" && <PanelTrello projectId={id} projectName={PROJECT.name} />}

                {ACTIVE_TAB === "runbooks" && <PanelRunbooks projectId={id} />}

                {ACTIVE_TAB === "health" && <PanelProjectHealth projectId={id} />}

                {ACTIVE_TAB === "credentials" && <PanelCredentials project={PROJECT} />}

                {ACTIVE_TAB === "links" && <PanelLinks projectId={id} />}

                {ACTIVE_TAB === "info" && <PanelProjectInfo project={PROJECT} />}

                {ACTIVE_TAB === "guides" && <PanelGuides projectId={id} scopeName={PROJECT.name} />}

            </div>

            <ProjectExportModal isOpen={useToolModal === "export"} onClose={() => setToolModal(null)} project={PROJECT} />
            <ProjectRelationsModal isOpen={useToolModal === "relations"} onClose={() => setToolModal(null)} project={PROJECT} />
            <ProjectTagsModal isOpen={useToolModal === "tags"} onClose={() => setToolModal(null)} project={PROJECT} />
            <ProjectHistoryModal isOpen={useToolModal === "history"} onClose={() => setToolModal(null)} project={PROJECT} onRestored={onLoadProject} />

            <ProjectFormModal
                isOpen={useIsEditOpen}
                project={PROJECT}
                companies={COMPANIES ?? []}
                onClose={() => setIsEditOpen(false)}
                onSaved={(saved) => mutateLocal({ id: PROJECT.id, patch: saved })} />
        </ViewLayout>
    );
};
